import type { KeyboardEvent, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Icona } from '@/components/Icona';
import { useMe } from '@/permisos/MeContext';
import type { TipusNode } from '../marc/estructura';
import type { NodeVisible } from './arbreModel';
import { clauNode, TIPUS_FILL, type Seleccio } from './suport';

/** Arbre Departament › Àrea › Servei amb plegat, selecció i «＋» per crear fills. */
export function Arbre({
  nodes,
  seleccio,
  oberts,
  totObert,
  onCommuta,
  onSelecciona,
  onNou,
}: {
  nodes: NodeVisible[];
  seleccio: Seleccio | null;
  oberts: Set<string>;
  /** Amb cerca o filtre, tot es mostra desplegat. */
  totObert: boolean;
  onCommuta: (clau: string) => void;
  onSelecciona: (s: Seleccio) => void;
  onNou: (tipus: TipusNode, pareId: number) => void;
}) {
  const { t } = useTranslation('administracio');
  const { pot } = useMe();
  const potCrear = pot('cai.organitzacio', 'ALTA');

  const fila = (n: NodeVisible) => {
    const clau = clauNode(n);
    const obert = totObert || oberts.has(clau);
    const teFills = n.fills.length > 0;
    const sel = seleccio?.tipus === n.tipus && seleccio.id === n.id;
    const tipusFill = TIPUS_FILL[n.tipus];
    const tecla = (e: KeyboardEvent) => {
      if (e.target !== e.currentTarget) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onSelecciona(n);
      } else if (e.key === 'ArrowRight' && teFills && !obert) {
        e.preventDefault();
        onCommuta(clau);
      } else if (e.key === 'ArrowLeft' && teFills && obert && !totObert) {
        e.preventDefault();
        onCommuta(clau);
      }
    };
    const atura = (accio: () => void) => (e: MouseEvent) => {
      e.stopPropagation();
      accio();
    };
    return (
      <li key={clau} role="none">
        <div
          role="treeitem"
          aria-level={n.nivell}
          aria-selected={sel}
          aria-expanded={teFills ? obert : undefined}
          tabIndex={0}
          data-node={clau}
          className={`est-node est-node--n${n.nivell}${sel ? ' is-sel' : ''}${n.actiu ? '' : ' is-inactiu'}`}
          onClick={() => onSelecciona(n)}
          onKeyDown={tecla}
        >
          {teFills ? (
            <button
              type="button"
              tabIndex={-1}
              className={`est-node__plega${obert ? ' is-obert' : ''}`}
              aria-label={
                obert
                  ? t('estructura.plega', { nom: n.nom })
                  : t('estructura.desplega', { nom: n.nom })
              }
              disabled={totObert}
              onClick={atura(() => onCommuta(clau))}
            >
              <Icona nom="chevron-right" />
            </button>
          ) : (
            <span className="est-node__plega" aria-hidden="true" />
          )}
          {n.tipus === 'DEPARTAMENT' ? (
            <Icona nom="building" className="est-node__icona" />
          ) : (
            <span
              className={`node-punt node-punt--${(n.tipusArea ?? 'GUARDIA_IT').toLowerCase()}`}
              aria-hidden="true"
            />
          )}
          <span className="est-node__nom">{n.nom}</span>
          {n.compte ? <span className="est-node__compte">{n.compte}</span> : null}
          {tipusFill && potCrear ? (
            <button
              type="button"
              tabIndex={-1}
              className="est-node__nou"
              title={t('estructura.afegeixFill', {
                tipus: t(`estructura.tipus.${tipusFill}`),
                nom: n.nom,
              })}
              aria-label={t('estructura.afegeixFill', {
                tipus: t(`estructura.tipus.${tipusFill}`),
                nom: n.nom,
              })}
              onClick={atura(() => onNou(tipusFill, n.id))}
            >
              <Icona nom="plus" />
            </button>
          ) : null}
        </div>
        {teFills && obert ? (
          <ul role="group" className="est-arbre__grup">
            {n.fills.map(fila)}
          </ul>
        ) : null}
      </li>
    );
  };

  return (
    <ul className="est-arbre" role="tree" aria-label={t('estructura.arbre')}>
      {nodes.map(fila)}
    </ul>
  );
}
